<template>
  <toolbar-container>
    <template #toolbar>
      <reservations-toolbar
        :daynote-visible="compact && canvasMaximized || !compact"
        :print-visible="compact && canvasMaximized || !compact"
        :reservations="reservations"
        waitlist-popout
      >
        <!-- Show when Splitview is active -->
        <template v-if="!canvasMaximized">
          <v-select
            :value="tab"
            :class="['fp-tab-select pl-0 pr-2', {'compact': compact }]"
            hide-details
            return-object
            :dense="compact"
            :items="tabs"
            @input="tabChanged"
          />
          <v-spacer v-if="compact && !canvasMaximized" />
          <v-btn
            small
            icon
            class="mr-0 pr-0 ml-5"
            @click="canvasMaximized = true"
          >
            <v-icon>
              mdi-chevron-left
            </v-icon>
          </v-btn>
        </template>
        <!-- ///  -->
        <!-- Show When Maximized -->
        <template v-else>
          <v-btn
            small
            icon
            class="mr-0 pr-0"
            @click="canvasMaximized = false"
          >
            <v-icon>
              mdi-chevron-right
            </v-icon>
          </v-btn>
        </template>
        <!-- /// -->
        <v-slider
          v-if="compact && canvasMaximized || !compact"
          v-model="timeSlot"
          class="px-2 mt-1"
          hide-details
          :max="timeSlotMax"
          :min="timeSlotMin"
          style="width: 100%"
          @input="timeSlotChanged"
        >
          <template #append>
            <v-btn
              id="time-tracking-btn"
              small
              :depressed="timeTracking"
              elevation="0"
              :outlined="!timeTracking"
              color="primary"
              :disabled="!isToday"
              @click="timeTrackingClick"
            >
              {{ $localized.hourMinTextFrom24hr(timeSlotText) }}
            </v-btn>
            <v-select
              v-model="zoom"
              class="ml-5"
              :style="`min-width:${$vuetify.breakpoint.xsOnly ? 42 : 78 }px!important;
                      max-width:${$vuetify.breakpoint.xsOnly ? 42 : 78 }px!important`"
              append-icon=""
              :prepend-inner-icon="$vuetify.breakpoint.xsOnly ? '' : 'mdi-magnify-plus-outline'"
              hide-details
              :items="zoomLevels"
            />
          </template>
        </v-slider>
      </reservations-toolbar>
    </template>
    <table-allocation-CTA />
    <div class="fp-wrapper d-flex h-100">
      <!-- SIDEBAR -->
      <template v-if="!canvasMaximized">
        <floorplan-view-sidebar
          :class="['fp-view-sidebar', {'compact': compact }]"
          :reservations-by-time="reservationsByTime"
          :selected-res-id="selectedResId"
          :is-loaded="isLoaded"
          @selected="reservationSelected"
        />
        <!-- /// -->
        <v-divider
          vertical
          style="z-index: 0"
        />
      </template>
      <!-- CANVAS -->
      <floorplan-canvas
        :tab-items="tabItems"
        :ongoing-reservations="ongoingReservationsForTabItem"
        :upcoming-reservations="upcomingReservationsForTabItem"
        :selected-tab-item-ids="selectedTabItemIds"
        :overbooked-tab-item-ids="overbookedTabItemIds"
        :zoom="zoom"
        :is-loaded="isLoaded"
        @selected="tabItemSelected"
        @setup-floorplan="setupFloorplan"
      />
      <!-- /// -->
    </div>
  </toolbar-container>
</template>

<script lang="ts">

import Vue from 'vue';
import ReservationsToolbar from '@/components/views/main/ReservationsToolbar.vue';
import FloorplanCanvas from '@/components/views/floorplan/FloorplanCanvas.vue';
import FloorplanViewSidebar from '@/components/views/floorplan/FloorplanViewSidebar.vue';
import TabItem from '@/model/TabItem';
import Reservation from '@/model/Reservation';
import Tab from '@/model/Tab';
import { mapBySlotToMapByTimeText, toSortedMapBySlot } from '@/services/reservations-utils';
import {
  DAY_TIME_SLOTS_COUNT, MORNING_TIME_SLOTS_COUNT,
  minutesFromDate, minutesFromSlot, slotFromMinutes, niceTimeTextFromSlot,
} from '@/services/time-utils';
import { tryCloseEditReservation, tryEditReservation } from '@/services/reservation-edit-utils';
import storage from '@/services/local-storage';
import { clamp } from '@/services/common';
import KeyboardControl from '@/mixins/keyboard-control';
import { durationForReservation } from '@/services/reservation-utils';
import TableAllocationCTA from '@/components/util/TableAllocationCTA.vue';

export default Vue.extend({
  name: 'Floor',
  components: {
    FloorplanCanvas,
    FloorplanViewSidebar,
    ReservationsToolbar,
    TableAllocationCTA,
  },
  mixins: [KeyboardControl],
  data() {
    const isToday = this.$tstore.getters.isToday as boolean;
    return {
      zoom: 1,
      zoomLevels: [
        { value: 0.25, text: '25%' },
        { value: 0.5, text: '50%' },
        { value: 0.75, text: '75%' },
        { value: 1, text: '100%' },
        { value: 1.25, text: '125%' },
        { value: 1.50, text: '150%' },
        { value: 1.75, text: '175%' }],
      timeSlot: MORNING_TIME_SLOTS_COUNT,
      selectedTabItemIds: undefined as number[] | undefined,
      selectedResId: undefined as number | undefined,
      canvasMaximized: false,
      timeTracking: isToday, // should be false if not today
      timeTrackingInterval: 0 as number,
    };
  },
  computed: {
    editedReservationId(): number | undefined {
      return this.$tstore.getters.editReservation?.id;
    },
    isLoaded(): boolean {
      return this.$tstore.getters.isLoaded;
    },
    tabs(): Tab[] {
      return this.$tstore.state.settings.tabs;
    },
    tab(): Tab | null {
      return this.$tstore.state.filter.floorplanTab ?? this.tabs.find(Boolean) ?? null;
    },
    tabItems(): TabItem[] {
      return this.tab?.tabItems ?? [];
    },
    reservations(): Reservation[] {
      const all = this.$tstore.getters.dayValidReservationsAndBlocks as Reservation[];
      console.log('reservations: ', all.filter((o) => o.tab === this.tab));
      return all.filter((o) => o.tab === this.tab);
    },
    reservationsByTime(): Map<string, Reservation[]> {
      const reservationsBySlot = toSortedMapBySlot(this.reservations);
      const reservationsByTime = mapBySlotToMapByTimeText(reservationsBySlot);
      return reservationsByTime;
    },
    ongoingReservationsForTabItem(): Map<number, Reservation[]> {
      const map = new Map<number, Array<Reservation>>();
      this.reservations.forEach((r) => {
        if (r.slotBegin > this.timeSlot || r.slotEnd < this.timeSlot) return; // not ongoing
        r.tabItems.forEach((ti) => {
          map.set(ti.id, (map.get(ti.id) ?? []).concat(r));
        });
      });
      return map;
    },
    upcomingReservationsForTabItem(): Map<number, Reservation[]> {
      const duration = durationForReservation([], this.tab ?? undefined);

      const map = new Map<number, Array<Reservation>>();
      this.reservations.forEach((r) => {
        if (r.slotBegin <= this.timeSlot) return; // past or ongoing
        if (minutesFromSlot(r.slotBegin - this.timeSlot) >= duration) return; // far enough

        r.tabItems.forEach((ti) => {
          map.set(ti.id, (map.get(ti.id) ?? []).concat(r));
        });
      });
      return map;
    },
    overbookedTabItemIds(): number[] {
      const ovebooked = new Set<number>();
      this.ongoingReservationsForTabItem.forEach((rs, tid) => { if (rs && rs.length > 1) ovebooked.add(tid); });
      return Array.from(ovebooked.values());
    },
    timeSlotText(): string {
      return niceTimeTextFromSlot(this.timeSlot);
    },
    timeSlotMin(): number {
      return MORNING_TIME_SLOTS_COUNT;
    },
    timeSlotMax(): number {
      return DAY_TIME_SLOTS_COUNT - 1;
    },
    compact(): boolean {
      return this.$vuetify.breakpoint.xsOnly;
    },
    date(): Date {
      return this.$tstore.state.update.date;
    },
    isToday(): boolean {
      return this.$tstore.getters.isToday;
    },
  },
  watch: {
    timeTracking() {
      this.timeTrackerUpdate(this.timeTracking);
    },
    reservations: {
      deep: true,
      handler() {
        this.timeSlot = this.isToday ? this.nowSlot() : this.slotForFirstReservation();
      },
    },
    date() {
      console.log('watch: date update');
      this.timeTracking = this.$tstore.getters.isToday;
      this.timeSlot = this.isToday ? this.nowSlot() : this.slotForFirstReservation();
    },
    zoom() {
      storage.setFloorplanZoom(this.zoom);
    },
    editedReservationId() {
      // deselect table when reseditor has been closed
      if (this.editedReservationId === undefined) {
        this.selectedTabItemIds = [];
      }
    },
  },
  beforeDestroy() {
    this.timeTrackerUpdate(false);
  },
  mounted() {
    this.tab = this.tabs[0] ?? null;
    this.timeSlot = this.isToday ? this.nowSlot() : this.slotForFirstReservation();
    this.timeTrackerUpdate(this.timeTracking);
    this.canvasMaximized = this.compact;

    const zoom = storage.getFloorplanZoom();
    if (zoom) this.zoom = zoom;
  },
  methods: {
    async tabItemSelected(tabItemId?: number, reservations?: Reservation[]) {
      const reservation = reservations?.find(Boolean);
      this.selectedResId = reservation?.id;

      if (reservation) {
        this.selectedTabItemIds = reservation.tabItems.map((t) => t.id);
        if (this.$tstore.getters.editReservation?.id === reservation?.id) {
          await tryCloseEditReservation();
        } else {
          await tryEditReservation(this.$router, reservation);
        }
      } else if (tabItemId !== undefined) {
        const tabItems = this.tabItems.filter((t) => t.id === tabItemId);
        const partySize = tabItems.map((ti) => ti.seats).reduce((prev, next) => prev + next);
        const res = await tryEditReservation(this.$router, undefined, {
          tab: this.tab ?? undefined,
          tabItems,
          timeSlot: this.timeSlot,
          partySize,
        });
        if (res) {
          this.selectedTabItemIds = [tabItemId];
        }
      } else if (await tryCloseEditReservation()) {
        this.selectedTabItemIds = [];
      }
    },
    async reservationSelected(reservationId: number) {
      this.selectedResId = reservationId;
      const reservation = this.reservations.find((r) => r.id === reservationId);

      if (reservation === undefined || reservation.tabItems.length === 0) {
        this.selectedTabItemIds = [];
      } else {
        this.selectedTabItemIds = reservation.tabItems.map((t) => t.id);
        this.timeSlot = reservation.slotBegin;
        this.timeTracking = false;
      }

      await tryCloseEditReservation();
    },
    timeSlotChanged(timeSlot: number) {
      this.timeTracking = false;
      this.selectedResId = undefined;
      this.selectedTabItemIds = [];
    },
    tabChanged(tab: Tab) {
      this.$tstore.dispatch('applyFilter', { floorplanTab: tab });
    },
    setupFloorplan() {
      this.$router.push(`/settings/sections/${this.tab!.id}?floorplan=true`);
    },
    nowSlot() {
      const date = new Date();
      const time = minutesFromDate(date);
      const slot = slotFromMinutes(time);
      return clamp(slot, this.timeSlotMin, this.timeSlotMax);
    },
    timeTrackingClick() {
      this.timeTracking = !this.timeTracking;
      if (this.timeTracking && this.isToday) {
        this.timeSlot = this.nowSlot();
      }
    },
    timeTrackerUpdate(start: boolean) {
      if (start) {
        this.timeTrackingInterval = window.setInterval(() => {
          this.handleTimeTracker();
        }, 10000);
      } else {
        window.clearInterval(this.timeTrackingInterval);
      }
    },
    handleTimeTracker() {
      if (this.timeTracking && this.isToday) {
        this.timeSlot = this.nowSlot();
      }
    },
    slotForFirstReservation(): number {
      if (this.reservations.length === 0) {
        return MORNING_TIME_SLOTS_COUNT;
      }
      const sorted = toSortedMapBySlot(this.reservations);
      const firstSlot = sorted.keys().next().value;
      return firstSlot;
    },
  },
});

</script>

<style lang="scss" scoped>

  .fp-wrapper {
    height: calc(100% - 57px);

    &.compact {
      height: calc(100% - 48px);
    }
  }

  .fp-view-sidebar {
    min-width: 400px;
    width: 400px;
    max-width: 400px;
    overflow: auto;
    height: 100%;

    &.compact {
      width: 100%;
      min-width: 100%;
      max-width: 100%;
    }
  }

  #time-tracking-btn {
    box-sizing: border-box;
    width: 58px;

    &.v-btn--has-bg.theme--dark {
      color: $grey-90-color !important;
    }

    &.v-btn--disabled {
      color: $grey-75-color !important;

      &.theme--dark {
        color: $grey-50-color !important;
      }
    }
  }

  .fp-tab-select {
    max-width: 380px;
    min-width: 380px;

    &.compact {
      width: 100%;
      min-width: 90%;
      max-width: 100%;
    }
  }

</style>
